import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import type { WebTask } from '../../../lib/tasks';
import { outreachRowTitle, presentSharedFile } from '../../../lib/outreach-rows';
import { AgentDot } from '../../AgentDot';
import { Modal } from '../../Modal';
import { Button, Typography } from '../ui';
import { useContactBook, useContactEntryQuery, type ContactId } from '../../../lib/contact-book';
import { contactDisplayName } from '../../../lib/contact-rows';
import { composerVisible, mergeTranscript, type WireOwnerEvent } from '../../../lib/outreach-lane';
import { ownerNoteActionVisible } from '../../../lib/owner-note-refresh';
import { outreachSiblings } from '../../../lib/outreach-thread-stepper';
import { stageCard, threadByOutreachId, threadStage, uniqueThreads, type OutreachStage } from '../../../lib/outreach-stage';
import { StageCard } from '../contacts/StageCard';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { StackedSharedFileViewer } from '../files/StackedSharedFileViewer';
import { OutreachKindBadge } from './OutreachKindBadge';
import { OutreachClose } from './OutreachClose';
import { OutreachThreadTranscript, OwnerOnlyCaption, type TranscriptMessage } from '../outreach/OutreachThreadTranscript';
import { TellAgentComposer } from '../outreach/TellAgentComposer';
import { ThreadStepper, useScrollTopOnStep, type ThreadSibling } from '../outreach/ThreadStepper';
import styles from './outreach-thread.module.css';

/**
 * The correspondence behind an inbound outreach row — who said what, and when — plus the one thing anyone
 * opening a message wants: a way to answer it, which is the "Tell {agent}" composer.
 *
 * Its STAGE comes from the contact book, never from here: the row is bridge-fed (`/api/outreaches`), so it
 * joins the contact-book head by outreach id (`threadByOutreachId` over `useContactBook('')`, warmed on app
 * mount by `prefetchContactBook`). A hit pins the stage card and names the speakers; a miss renders the
 * correspondence as before, with Close gated on the bridge's own status.
 *
 * The owner's lane (what they told their agent, chose, approved…) rides only the PERSON-scoped read, so
 * a joined thread is re-read person-scoped (`useContactEntryQuery`) for its `ownerEvents`; that same read
 * is what the composer re-runs after a send.
 *
 * It renders the messages IN PLACE rather than opening the conversation they were delivered into, and
 * that is not a shortcut: an inbound outreach lands in a SCHEDULER conversation created for it on the
 * receiving side, which is not in the user's chat list, so navigating to `?conversation=<id>` finds
 * nothing to select and lands on the root — which is exactly what a click used to do.
 *
 * The messages render through the shared `OutreachThreadTranscript` — the same speaker model, sides, bubble
 * styles, day dividers and clock times as the Contacts viewer — so a thread reads the same wherever it is
 * opened. A message written before per-message dates existed has NO date, and renders without one instead
 * of borrowing the thread's.
 *
 * It renders its OWN <Modal>, because the drill-in host (`TaskSidePanel`) is a bare `{modal}` slot —
 * every detail view owns its scrim, close, Escape stack and portal. Written after the side-panel →
 * modal cutover (#7556) converted the other eight, this one shipped as a naked <div> and therefore
 * painted itself unstyled at the top of the page instead of opening. The header stays in the BODY
 * rather than the Modal's `title` so the AgentDot can sit beside it; `labelledBy` points the dialog's
 * accessible name at that heading. `fullScreenOnSmall` is the Modal default, so narrow viewports get
 * the full-screen sheet with a leading back chevron and wide gets the centered card.
 *
 * **Previous / Next.** `siblings` is the opener's row list — its threads in on-screen order, received and
 * (on Home) sent mixed — and the header's `ThreadStepper` steps through it without closing the dialog (its
 * rules, keys and BI are there). A step to another received thread REPLACES the stack's top entry with this
 * same component, so the Modal stays mounted (no close/reopen animation); a step onto a sent row swaps in
 * that row's own `OutreachCardModal`, which carries the same stepper. The body is keyed on the thread, which
 * resets the composer's draft and the optimistic lines, and the scroll returns to the top.
 */
export function OutreachThreadModal({ task, siblings }: { task: WebTask; siblings?: readonly ThreadSibling[] }) {
  const { closeModal } = useTaskSidePanel();
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useScrollTopOnStep(bodyRef, task.id);

  return (
    <Modal open onClose={closeModal} size="medium" maxHeightPx={650} scrollBody labelledBy="outreach-thread-title">
      <div ref={bodyRef}>
        <OutreachThreadBody
          key={task.id}
          task={task}
          stepper={<ThreadStepper siblings={siblings} currentId={task.id} page={TASKS_PAGE} className={styles.stepper} />}
        />
      </div>
    </Modal>
  );
}

/** A received thread as a stepper row: it opens this dialog. */
export function receivedThreadSibling(task: WebTask): ThreadSibling {
  return { id: task.id, render: (siblings) => <OutreachThreadModal task={task} siblings={siblings} /> };
}

/** The task lists' rows (inbound outreach only), as stepper rows for this dialog. */
export function receivedThreadSiblings(tasks: readonly WebTask[]): ThreadSibling[] {
  return outreachSiblings(tasks, (task) => (task.rowKind === 'outreach' ? task : undefined)).map(receivedThreadSibling);
}

/** One thread's content — keyed on the thread by the dialog, so a step starts it fresh. */
function OutreachThreadBody({ task, stepper }: { task: WebTask; stepper: ReactNode }) {
  const { t } = useTranslation();
  const { openModal, closeModal } = useTaskSidePanel();
  const entries = task.outreachMessages ?? [];
  // The SAME title rule the card and the island row read, so the thread a share opens into is headed by
  // the file rather than by "Message from Dafna" while the row that opened it names the file. The old
  // fallback here was `tasks.untitledTask` — a TASK string on a row that is not a task, and the one
  // place on this surface that still disagreed with the phones' `tasks.outreach.untitled`.
  const rowTitle = outreachRowTitle(task);
  const title =
    rowTitle.kind === 'file'
      ? rowTitle.fileName
      : rowTitle.kind === 'fileUntitled'
      ? t('tasks.outreach.sharedFileUntitled')
      : rowTitle.kind === 'sender'
      ? t('tasks.outreach.title', { name: rowTitle.name })
      : t('tasks.outreach.untitled');
  // The presence gate, shared: a notice that names no file must not render a View that opens nothing.
  const shared = presentSharedFile(task.sharedFile);
  const agent = useAgentName();
  const { logPageClickBi } = useGenericBi();
  // The join — the ONE stage computation stays in `lib/outreach-stage.ts`.
  const book = useContactBook('');
  const joined = threadByOutreachId(book.kind === 'ready' ? book.contacts : [], task.id);
  // The bridge row's sender is the name this outreach carries for them — the shared chain's step 2.
  const contactName = joined
    ? contactDisplayName(joined.entry.contact, joined.entry.channels, t('contacts.row.unnamed'), task.senderName)
    : '';
  // A miss keeps today's rule: the bridge's own COMPLETED is Done, anything else is open.
  const stage: OutreachStage = joined ? threadStage(joined.thread) : task.status === 'COMPLETED' ? 'done' : 'handling';
  // The person-scoped read of the joined contact — the only read that carries the owner's lane.
  const joinedPersonId = joined?.entry.contact?.personId?.trim();
  const joinedRecipientId = joined?.entry.recipientId?.trim();
  const personScope: ContactId = joinedPersonId ? { personId: joinedPersonId } : joinedRecipientId ? { recipientId: joinedRecipientId } : { personId: '' };
  const person = useContactEntryQuery(personScope);
  const personThread = person.entry ? uniqueThreads(person.entry).find(({ thread }) => thread.outreachId === task.id)?.thread : undefined;
  const [optimistic, setOptimistic] = useState<WireOwnerEvent[]>([]);
  // When the join hits, the person read's OWN messages are the transcript: they carry `author`,
  // `senderName` and `answeredByAgent`, and they are what the composer's re-read refreshes — so a send shows
  // up here. The bridge's rows remain the fallback for a miss (and while the person read is in flight); they
  // carry only the side, plus the joined thread's `author` when it holds the same messages.
  const personMessages = personThread?.messages && personThread.messages.length > 0 ? personThread.messages : undefined;
  const joinedMessages = joined?.thread.messages;
  const messages: TranscriptMessage[] = personMessages
    ? personMessages
    : entries.map((m, i) => ({
        text: m.text,
        sentAt: m.at,
        fromMe: m.mine,
        author: joinedMessages && joinedMessages.length === entries.length ? joinedMessages[i]?.author : undefined,
        senderName: joinedMessages && joinedMessages.length === entries.length ? joinedMessages[i]?.senderName : undefined,
      }));
  const transcript = mergeTranscript(messages, personThread?.ownerEvents, optimistic);
  const composerName = contactName || (rowTitle.kind === 'sender' ? rowTitle.name : '') || t('contacts.row.unnamed');

  return (
      <div className={styles.body}>
        <header className={styles.header}>
          <AgentDot id={task.id} size={15} />
          <h2 className={styles.title} id="outreach-thread-title">
            {title}
          </h2>
          {stepper}
        </header>
        {/* Says what this is before the thread does: a message, and one that came IN. The card carries
            the same badge, from the same component, so the two surfaces cannot describe the row
            differently. */}
        <div className={styles.kindRow}>
          <OutreachKindBadge />
        </div>

        {/* A SHARE announces a file; an ordinary outreach carries what a person wrote. When the notice
            names one, the file gets said out loud and the reader gets the action they actually came
            for — opening it — instead of a reply box being the only thing on offer. The reply box stays:
            a share is still a message someone may answer.

            Pushed onto the modal stack rather than swapped in, so closing the file returns to the thread
            the reader was reading; `backTo` names that destination, per the stack's rule that an
            unlabelled push degrades to no back at all. */}
        {shared ? (
          <div className={styles.sharedFile}>
            <Typography variant="p2" className={styles.sharedFileName} dir="auto">
              {shared.fileName || t('tasks.outreach.sharedFileUntitled')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const fileId = shared.fileId;
                logPageClickBi({ ...TASKS_PAGE, button_name: 'open_shared_file', task_id: task.id });
                openModal(
                  <StackedSharedFileViewer fileId={fileId} fileName={shared.fileName} />,
                  `shared-file:${fileId}`,
                  { backLabel: t('common.backTo', { title }) },
                );
              }}
            >
              {t('tasks.outreach.viewFile')}
            </Button>
          </div>
        ) : null}

        {joined ? <StageCard thread={joined.thread} contactName={contactName} page={TASKS_PAGE.page_name} /> : null}
        {joined && stageCard(joined.thread) ? <OwnerOnlyCaption /> : null}

        {transcript.length === 0 ? (
          <p className={styles.empty}>{t('tasks.outreach.emptyThread')}</p>
        ) : (
          <OutreachThreadTranscript
            messages={messages}
            transcript={transcript}
            // This dialog opens a RECEIVED outreach row, so a miss reads as inbound.
            ctx={{ contactName: composerName, direction: joined?.direction ?? 'inbound', channel: joined?.thread.channel }}
            maestroAgentName={agent.name}
            outreachId={task.id}
            page={TASKS_PAGE.page_name}
            contactPhoto={joined?.entry.contact?.effectiveAvatarUrl}
            avatarKey={joinedPersonId || composerName}
          />
        )}

        {/* Answering is the first thing anyone opening a message wants to do, so it is here rather than a
            step away. "Tell {agent}" hands the owner's words to their own agent, which sends them on this
            thread or acts on them; the dialog stays open and the note shows in the transcript. */}
        {composerVisible({ outreachId: task.id }, false) ? (
          <TellAgentComposer
            scope={{ kind: 'thread', outreachId: task.id, contactName: composerName, stage: joined?.thread.stage }}
            page={TASKS_PAGE.page_name}
            // The composer re-reads the shared set (`rereadAfterOwnerAction`: contact-book stages + the
            // bridge's outreach rows behind the Home and task rows) on every tick and once more on unmount;
            // this is only the dialog's own person read on top.
            refetch={joinedPersonId || joinedRecipientId ? () => person.refetch() : undefined}
            isSettled={(noteId) => ownerNoteActionVisible(personThread, noteId)}
            onSent={(event) => setOptimistic((prev) => [...prev, event])}
          />
        ) : null}

        {/* Close — the owner finishing the conversation. Offered on any thread that is not Done (it renders
            nothing then). It sits UNDER the composer because replying is the primary act; this is the way
            out, not the offer. Non-optimistic: write, re-read, then dismiss. */}
        <OutreachClose outreachId={task.id} stage={stage} onClosed={closeModal} />
      </div>
  );
}
